import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Chip, CircularProgress, Typography } from "@mui/material";
import theme from "../styles/theme";
import { fetchVolleySchemas } from "../api";

// Page admin : vue d'ensemble des 6 rotations (P1 à P6) en 5-1, chacune affichée sur son
// terrain avec les positions de l'ÉTAPE 1 du schéma de réception correspondant.

type PlayerKey = "Pass" | "Poi" | "Lib" | "R4" | "C1" | "C2";
type SubstituteKey = "Sub_Pass" | "Sub_Poi" | "Sub_Lib" | "Sub_R4" | "Sub_C1" | "Sub_C2";
type PuckId = string;
type Pos = { nx: number; ny: number };
type StepRaw = { label?: string; positions?: Partial<Record<PlayerKey, Pos>>; substitutes?: Partial<Record<SubstituteKey, Pos>> };
type Schema = {
  id: string;
  name: string;
  type: "service" | "reception";
  playerCount: 4 | 6;
  tactic: string;
  steps: StepRaw[];
};

const PC: Record<PlayerKey, { bg: string }> = {
  Pass: { bg: "#ef4444" },
  Poi: { bg: "#8b5cf6" },
  Lib: { bg: "#1a1a1a" },
  R4: { bg: "#f59e0b" },
  C1: { bg: "#3b82f6" },
  C2: { bg: "#ec4899" },
};

const P6: PlayerKey[] = ["Pass", "Poi", "Lib", "R4", "C1", "C2"];
const SUB6: SubstituteKey[] = ["Sub_Pass", "Sub_Poi", "Sub_Lib", "Sub_R4", "Sub_C1", "Sub_C2"];

const MX = 0.11,
  MY = 0.08,
  LW = 1 - MX * 2,
  LH = 1 - MY * 2;
const W = 320;
const COURT_H = 440;
const PUCK_R = 16;

function baseRole(p: PuckId): PlayerKey {
  return (p.startsWith("Sub_") ? p.slice(4) : p) as PlayerKey;
}

function getSchemasScope(): string {
  // Scope unique : coachs et joueuses lisent/écrivent le même espace.
  return "global";
}

function isOnCourt(pos: Pos): boolean {
  const margin = 0.015;
  return pos.nx >= MX - margin && pos.nx <= MX + LW + margin && pos.ny >= MY - margin && pos.ny <= MY + LH + margin;
}

// Pastilles réellement sur le terrain à l'étape donnée (titulaires + remplaçantes jouées).
function onCourtRoster(step: StepRaw): { puckId: PuckId; pos: Pos }[] {
  const roster: { puckId: PuckId; pos: Pos }[] = [];
  P6.forEach((k) => {
    const pos = step.positions?.[k];
    if (pos && isOnCourt(pos)) roster.push({ puckId: k, pos });
  });
  SUB6.forEach((k) => {
    const pos = step.substitutes?.[k];
    if (pos && isOnCourt(pos)) roster.push({ puckId: k, pos });
  });
  return roster;
}

function CourtBg() {
  const lx = MX * W,
    ly = MY * COURT_H,
    lw = LW * W,
    lh = LH * COURT_H;
  const netY = ly + lh * 0.12;
  const m3Y = ly + lh * 0.42;
  return (
    <>
      <rect x={0} y={0} width={W} height={COURT_H} rx={8} fill="#0b2e4a" />
      <rect x={lx} y={ly} width={lw} height={lh} fill="#1565a8" />
      <rect x={lx} y={netY} width={lw} height={m3Y - netY} fill="rgba(255,140,30,.30)" stroke="rgba(255,130,0,.6)" strokeWidth={1} />
      <rect x={lx} y={m3Y} width={lw} height={ly + lh - m3Y} fill="rgba(0,200,170,.25)" stroke="rgba(0,200,170,.55)" strokeWidth={1} />
      {[1 / 3, 2 / 3].map((r, i) => (
        <line key={i} x1={lx + r * lw} y1={ly + 1} x2={lx + r * lw} y2={ly + lh - 1} stroke="rgba(255,255,255,.15)" strokeWidth={1} />
      ))}
      <line x1={lx} y1={m3Y} x2={lx + lw} y2={m3Y} stroke="rgba(255,255,255,.55)" strokeWidth={1.5} strokeDasharray="6,4" />
      <line x1={lx} y1={netY} x2={lx + lw} y2={netY} stroke="rgba(255,255,255,.85)" strokeWidth={3} />
      <rect x={lx} y={ly} width={lw} height={lh} fill="none" stroke="rgba(255,255,255,.5)" strokeWidth={2} />
    </>
  );
}

// Petit terrain en lecture seule affichant les pastilles d'une étape.
function MiniCourt({ roster }: { roster: { puckId: PuckId; pos: Pos }[] }) {
  return (
    <svg viewBox={`0 0 ${W} ${COURT_H}`} style={{ width: "100%", height: "auto", display: "block", borderRadius: 8 }}>
      <CourtBg />
      {roster.map(({ puckId, pos }) => {
        const base = baseRole(puckId);
        const cx = pos.nx * W,
          cy = pos.ny * COURT_H;
        return (
          <g key={puckId}>
            <circle cx={cx} cy={cy} r={PUCK_R} fill={PC[base].bg} stroke="rgba(255,255,255,.75)" strokeWidth={1.5} />
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={10.5} fontWeight={700} fill="#fff" fontFamily="monospace">
              {base === "C1" || base === "C2" ? "C" : base}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// Numéro de rotation (1-6) extrait du nom du schéma, ex: "P3 Reception" -> 3.
function rotationOf(name: string): number | null {
  const m = name.match(/P\s*([1-6])/i);
  return m ? Number(m[1]) : null;
}

export default function AdminRotations() {
  const scope = useMemo(() => getSchemasScope(), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schemas, setSchemas] = useState<Schema[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchVolleySchemas(scope);
        const mapped: Schema[] = (Array.isArray(raw) ? raw : [])
          .filter((s: any) => s && typeof s === "object")
          .map((s: any) => ({
            id: String(s.id ?? ""),
            name: String(s.name ?? "Schéma"),
            type: s.type === "service" ? "service" : "reception",
            playerCount: Number(s.playerCount) === 4 ? 4 : 6,
            tactic: typeof s.tactic === "string" ? s.tactic : "",
            steps: Array.isArray(s.steps) ? s.steps : [],
          }));
        // Comme dans le quiz : la liste est triée updated_at DESC et peut contenir plusieurs
        // versions d'un même schéma — on garde la 1re occurrence (= la plus récente).
        const seen = new Set<string>();
        const unique = mapped.filter((s) => {
          if (!s.id || seen.has(s.id)) return false;
          seen.add(s.id);
          return true;
        });
        if (!cancelled) setSchemas(unique);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Erreur de chargement des schémas.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope]);

  // Un schéma de réception 6 joueuses par rotation P1..P6 (préférence aux tactiques 5-1).
  const byRotation = useMemo(() => {
    const map = new Map<number, Schema>();
    const candidates = schemas.filter((s) => s.type === "reception" && s.playerCount === 6);
    const pick = (list: Schema[]) => {
      list.forEach((s) => {
        const rot = rotationOf(s.name);
        if (rot && !map.has(rot)) map.set(rot, s);
      });
    };
    pick(candidates.filter((s) => s.tactic.replace(/\s/g, "").includes("5-1")));
    pick(candidates); // complète avec le reste si une rotation n'a pas de schéma étiqueté 5-1
    return map;
  }, [schemas]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: 10 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main, mb: 0.5 }}>
        Rotations 5-1 — Étape 1
      </Typography>
      <Typography sx={{ color: theme.palette.text.secondary, mb: 2.5, fontSize: 14.5 }}>
        Les 6 positions de réception (P1 à P6), telles que définies à l'<b>Étape 1</b> de chaque schéma.
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={30} />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" },
            gap: { xs: 1.5, sm: 2 },
            maxWidth: 1400,
          }}
        >
          {[1, 2, 3, 4, 5, 6].map((rot) => {
            const schema = byRotation.get(rot);
            const step1 = schema?.steps.find((st) => st.label?.trim() === "Étape 1");
            const roster = step1 ? onCourtRoster(step1) : [];
            return (
              <Box
                key={rot}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1.25,
                  bgcolor: "background.paper",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 17 }}>P{rot}</Typography>
                  {schema?.tactic && <Chip label={schema.tactic} size="small" variant="outlined" sx={{ fontWeight: 700 }} />}
                </Box>
                {schema && roster.length > 0 ? (
                  <MiniCourt roster={roster} />
                ) : (
                  <Alert severity="warning" sx={{ fontSize: 12.5 }}>
                    {schema ? "Pas d'« Étape 1 » définie." : `Aucun schéma « P${rot} » trouvé.`}
                  </Alert>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
